import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TrialProvider } from './context/TrialContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import QuizGenerate from './pages/QuizGenerate';
import QuizTakeAll from './pages/QuizTakeAll';
import QuizResult from './pages/QuizResult';
import History from './pages/History';
import PublicQuiz from './pages/PublicQuiz';
import Pricing from './pages/Pricing';
import MyClasses from './pages/MyClasses';
import ClassView from './pages/ClassView';
import GroupDashboard from './pages/GroupDashboard';
import JoinGroup from './pages/JoinGroup';
import PaymentSuccess from './pages/PaymentSuccess';
import SolveQuestion from './pages/SolveQuestion';
import AdminDashboard from './pages/AdminDashboard';
import './App.css';

// Remove any injected badges
const removeBadges = () => {
  const selectors = [
    '#emergent-badge',
    '[id*="emergent"]',
    'a[href*="emergent.sh"]',
    'a[href*="emergent"]'
  ];
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => el.remove());
  });
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

function App() {
  // Remove injected badges on mount and periodically
  useEffect(() => {
    removeBadges();
    const interval = setInterval(removeBadges, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <TrialProvider>
        <SubscriptionProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/quiz/generate" element={<QuizGenerate />} />
          <Route path="/solve" element={<SolveQuestion />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route
            path="/classes"
            element={
              <ProtectedRoute>
                <MyClasses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes/:classId"
            element={
              <ProtectedRoute>
                <ClassView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/quiz/take/:quizId" element={<QuizTakeAll />} />
          <Route
            path="/shared/:shareToken"
            element={<PublicQuiz />}
          />
          <Route path="/quiz/result/:quizId" element={<QuizResult />} />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            }
          />
          <Route
            path="/group/dashboard"
            element={
              <ProtectedRoute>
                <GroupDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/join-group"
            element={
              <ProtectedRoute>
                <JoinGroup />
              </ProtectedRoute>
            }
          />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
        </BrowserRouter>
        </SubscriptionProvider>
        </TrialProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
